import React, { KeyboardEvent } from 'react'
import { Stack, StackProps } from '@mui/material'

export interface PfClickableCardProps extends Omit<StackProps, 'onClick' | 'onKeyDown' | 'role' | 'tabIndex'> {
  /** Called on a click anywhere on the card, or on Enter or Space while the card itself has focus. */
  onActivate: () => void
}

/**
 * A card that is a single button: the whole card takes focus and activates on click, Enter or Space. A key
 * aimed at a control inside it (an information button) stays that control's.
 *
 * @example
 * <PfClickableCard onActivate={() => onSelect(product)} sx={CATALOGUE_CARD_SX}>…</PfClickableCard>
 */
export const PfClickableCard: React.FC<PfClickableCardProps> = ({ onActivate, sx, children, ...rest }) => {
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return
    e.preventDefault()
    onActivate()
  }

  return (
    <Stack
      role="button"
      tabIndex={0}
      onClick={onActivate}
      onKeyDown={handleKeyDown}
      sx={{ cursor: 'pointer', outline: 'none', ...sx }}
      {...rest}
    >
      {children}
    </Stack>
  )
}
